import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { locationService } from '../services/api';
import PromoCodeModal from './PromoCodeModal';
import './SearchBar.css';

const SearchBar = ({ onSearch, initialValues = {} }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupLocation, setPickupLocation] = useState(initialValues.pickupLocation || '');
  const [pickupDate, setPickupDate] = useState(
    initialValues.pickupDate ? new Date(initialValues.pickupDate) : new Date()
  );
  const [pickupTime, setPickupTime] = useState(initialValues.pickupTime || '11:00');
  const [returnDate, setReturnDate] = useState(
    initialValues.returnDate ? new Date(initialValues.returnDate) : new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
  );
  const [returnTime, setReturnTime] = useState(initialValues.returnTime || '11:00');
  const [returnToDifferentLocation, setReturnToDifferentLocation] = useState(false);
  const [returnLocation, setReturnLocation] = useState('');
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await locationService.getAll();
        const activeLocations = (response.data.data || []).filter(loc => loc.active);
        setLocations(activeLocations);
        if (activeLocations.length > 0 && !pickupLocation) {
          setPickupLocation(activeLocations[0].name);
          setReturnLocation(activeLocations[0].name);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLocations();
  }, [pickupLocation]);

  // Validar valores iniciales cuando se cargan
  useEffect(() => {
    if (pickupDate && returnDate && pickupTime && returnTime) {
      validateDateTime(pickupDate, pickupTime, returnDate, returnTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validar fechas y horas en tiempo real
  const validateDateTime = (newPickupDate, newPickupTime, newReturnDate, newReturnTime) => {
    const pickupDateTime = new Date(newPickupDate);
    pickupDateTime.setHours(parseInt(newPickupTime.split(':')[0]), parseInt(newPickupTime.split(':')[1]), 0, 0);
    
    const returnDateTime = new Date(newReturnDate);
    returnDateTime.setHours(parseInt(newReturnTime.split(':')[0]), parseInt(newReturnTime.split(':')[1]), 0, 0);
    
    // Calcular diferencia en horas
    const diffInMs = returnDateTime - pickupDateTime;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      setDateError('La reserva debe ser de al menos 1 día completo (24 horas)');
      return false;
    }
    
    setDateError('');
    return true;
  };

  const handlePickupDateChange = (date) => {
    setPickupDate(date);
    validateDateTime(date, pickupTime, returnDate, returnTime);
  };

  const handlePickupTimeChange = (time) => {
    setPickupTime(time);
    validateDateTime(pickupDate, time, returnDate, returnTime);
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
    validateDateTime(pickupDate, pickupTime, date, returnTime);
  };

  const handleReturnTimeChange = (time) => {
    setReturnTime(time);
    validateDateTime(pickupDate, pickupTime, returnDate, time);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar antes de enviar
    if (!validateDateTime(pickupDate, pickupTime, returnDate, returnTime)) {
      return;
    }
    
    onSearch({
      pickupLocation,
      pickupDate: pickupDate.toISOString(),
      pickupTime,
      returnDate: returnDate.toISOString(),
      returnTime,
      returnToDifferentLocation,
      returnLocation: returnToDifferentLocation ? returnLocation : pickupLocation
    });
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      times.push(`${i.toString().padStart(2, '0')}:00`);
      times.push(`${i.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  if (loading) {
    return (
      <div className="search-bar">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Cargando...
        </div>
      </div>
    );
  }

  return (
    <div className="search-bar">
      <h3 className="search-bar-title">Modificar búsqueda</h3>
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-row">
          <div className="search-field location-field">
            <label>Lugar de entrega</label>
            <select
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
              className="location-select"
              disabled={locations.length === 0}
            >
              {locations.length === 0 ? (
                <option value="">No hay ubicaciones</option>
              ) : (
                locations.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="search-field date-field">
            <label>Fecha de entrega</label>
            <DatePicker
              selected={pickupDate}
              onChange={handlePickupDateChange}
              dateFormat="EEE, MMM dd"
              minDate={new Date()}
              className="date-input"
            />
          </div>

          <div className="search-field time-field">
            <label>Hora</label>
            <select value={pickupTime} onChange={(e) => handlePickupTimeChange(e.target.value)}>
              {generateTimeOptions().map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>

          <div className="search-field date-field">
            <label>Fecha de devolución</label>
            <DatePicker
              selected={returnDate}
              onChange={handleReturnDateChange}
              dateFormat="EEE, MMM dd"
              minDate={pickupDate}
              className="date-input"
            />
          </div>

          <div className="search-field time-field">
            <label>Hora</label>
            <select value={returnTime} onChange={(e) => handleReturnTimeChange(e.target.value)}>
              {generateTimeOptions().map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className="search-button">
            BUSCAR
          </button>
        </div>

        {dateError && (
          <div className="search-error-message">
            ⚠️ {dateError}
          </div>
        )}

        {returnToDifferentLocation && (
          <div className="search-row return-location-row">
            <div className="search-field location-field">
              <label>Lugar de devolución</label>
              <select
                value={returnLocation}
                onChange={(e) => setReturnLocation(e.target.value)}
                className="location-select"
                disabled={locations.length === 0}
              >
                {locations.length === 0 ? (
                  <option value="">No hay ubicaciones</option>
                ) : (
                  locations.map((location) => (
                    <option key={location.id} value={location.name}>
                      {location.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        )}

        <div className="search-options">
          <div className="search-checkbox">
            <input
              type="checkbox"
              id="differentLocation"
              checked={returnToDifferentLocation}
              onChange={(e) => setReturnToDifferentLocation(e.target.checked)}
            />
            <label htmlFor="differentLocation">Devolver en otro lugar</label>
          </div>
          
          <span className="promo-code-link" onClick={() => setShowPromoModal(true)}>
            Tengo un código promo
          </span>
        </div>
      </form>

      <PromoCodeModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
      />
    </div>
  );
};

export default SearchBar;
